import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useSearchParams } from "react-router-dom";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import Card from "../layout/Card";
import Filters from "../layout/Filters";
import { translateText } from "../../utils/translation";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import DonutChart from "../charts/radial/DonutChart";
import { getOverview } from "../../slices/oversight";
import VerticalBarChart from "../charts/bar/VerticalBarChart";
import { getDevicesConnectivityBatch } from "../../slices/devices";
import { getCurrencySymbol } from "../../utils/currency";
import NumericChart from "../charts/number/Numeric";
import { getColourPaletteHex } from "../../utils/colours";

export default function Overview() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const dateRangeTerm = searchParams.get("dateRange");

  // Fetch data from server
  useEffect(() => {
    dispatch(getOverview({ dateRange: dateRangeTerm }));
  }, [dispatch, dateRangeTerm]);

  // Set state
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const {
    devices: { connectivity },
  } = useAppSelector((state) => state.devices);
  const { data, loading, options } = useAppSelector((state) => state.oversight);
  const language = user === null ? "EN" : user.language;

  useEffect(() => {
    let deviceIds = [];
    if (data !== null) deviceIds = data.devices.map((x: any) => x.id);
    if (data !== null) {
      dispatch(getDevicesConnectivityBatch({ deviceIds, model: null }));
    }
  }, [dispatch, data]);

  function getGeneralMetadataForCharts() {
    let obj: { [key: string]: any } = {
      roomsWithDevices: [],
      numRequests: 0,
      roomsMadeRequests: [],
      actionableRequestsCount: [],
      contentCount: [],
      activeUsers: 0,
      paymentTransactionStatusCount: [],
      paymentTotalAmount: 0,
    };
    if (data === null) return obj;

    // Rooms with devices in
    const numRoomsWithDevices = data.devices.filter(
      (x: any) => x.room !== null,
    ).length;
    obj.roomsWithDevices = [
      {
        id: "Rooms with devices",
        label: "Rooms with devices",
        value: numRoomsWithDevices,
      },
      {
        id: "Rooms without devices",
        label: "Rooms without devices",
        value: data.rooms.length - numRoomsWithDevices,
      },
    ];

    // Number of requests
    obj.numRequests = data.requests.length;

    // Rooms that have made requests
    const roomsMadeRequests = data.rooms_with_requests.length;
    obj.roomsMadeRequests = [
      {
        id: "Rooms with requests",
        label: "Rooms with requests",
        value: roomsMadeRequests,
      },
      {
        id: "Rooms without requests",
        label: "Rooms without requests",
        value: data.rooms.length - roomsMadeRequests,
      },
    ];

    // Actionable request status count
    const actionableRequestsCount = data.actionable_requests.map((x: any) => ({
      id: options.request_status[x.status][language],
      label: options.request_status[x.status][language],
      Count: x.count,
    }));
    obj.actionableRequestsCount = actionableRequestsCount;

    // Content count
    const contentCount = data.content.map((x: any) => ({
      id: options.log_content[x.type][language],
      label: options.log_content[x.type][language],
      value: x.count,
    }));
    obj.contentCount = contentCount;

    // Active users
    obj.activeUsers = data.active_users.length;

    // Payment transaction status count
    const paymentTransactionStatusCount = data.transactions.map((x: any) => ({
      id: options.payment_status[x.status][language],
      label: options.payment_status[x.status][language],
      Count: x.count,
    }));
    obj.paymentTransactionStatusCount = paymentTransactionStatusCount;

    // Payment total amount
    obj.paymentTotalAmount = data.total_transaction_amount;

    return obj;
  }

  function getDeviceMetadata() {
    let obj: { [key: string]: any } = {
      devicesOnline: [],
    };
    if (connectivity.data === null) return obj;

    // Devices online
    const devicesOnline = connectivity.data.filter(
      (x: any) => x.online === true,
    ).length;
    obj.devicesOnline = [
      {
        id: "Devices online",
        label: "Devices online",
        Count: devicesOnline,
      },
      {
        id: "Devices offline",
        label: "Devices offline",
        Count: data.devices.length - devicesOnline,
      },
    ];
    return obj;
  }

  function formatDataForCharts() {
    // Get general metadata
    let obj: { [key: string]: any } = getGeneralMetadataForCharts();

    // Get devices metadata
    obj = { ...obj, ...getDeviceMetadata() };

    return obj;
  }

  function getTextSize(value: number) {
    const length = value.toString().length;
    if (length <= 3) return "9xl";
    if (length <= 6) return "8xl";
    if (length <= 8) return "7xl";
    return "4xl";
  }

  const formattedData = formatDataForCharts();

  return (
    <Template>
      {/* Header */}
      <Header text={translateText("overview_title", language)} />

      {/* Loading spinner */}
      {loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              queryParams={[["dateRange", dateRangeTerm]]}
              handlers={{
                dateRange: {
                  state: dateRange,
                  stateName: "N/A",
                  onChange: setDateRange,
                },
              }}
              loading={loading}
              baseUrl="/oversight/overview"
              showDateRange={true}
              showFilters={false}
              showSearchBar={false}
            />
            {data !== null ? (
              <div className="mx-1 mb-10">
                {/* Row 1 */}
                <div className="grid md:grid-cols-3 grid-cols-1 mt-6 md:space-x-8 space-x-0 md:space-y-0 space-y-8">
                  {/* Rooms with devices in */}
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full items-center justify-between space-x-6">
                      <div className="flex-1">
                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                          <h3 className="text-base font-semibold leading-6 text-black">
                            {translateText("rooms_with_devices", language)}
                          </h3>
                          <span className="text-xs text-secondary">
                            {translateText(
                              formattedData.roomsWithDevices.length === 0
                                ? "not_enough_data"
                                : "oversight_overview_rooms_with_devices_subtext",
                              language,
                            )}
                          </span>
                        </div>
                        <DonutChart
                          data={formattedData.roomsWithDevices}
                          className="mx-auto text-sm lg:h-64 h-48"
                          colours={[
                            getColourPaletteHex("light-success"),
                            getColourPaletteHex("light-danger"),
                          ]}
                        />
                      </div>
                    </div>
                  </Card>

                  {/* Number of requests */}
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full items-center justify-between space-x-6">
                      <div className="flex-1">
                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                          <h3 className="text-base font-semibold leading-6 text-gray-900">
                            {translateText("number_requests", language)}
                          </h3>
                          <span className="text-xs text-gray-400">
                            {translateText(
                              "oversight_overview_number_requests_subtext",
                              language,
                            )}
                          </span>
                        </div>
                        <NumericChart
                          value={formattedData.numRequests}
                          className="lg:h-64 md:h-48 h-32"
                          colour="info"
                        />
                      </div>
                    </div>
                  </Card>

                  {/* Devices online / offline */}
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full h-full items-center justify-between space-x-6">
                      {connectivity.loading ? (
                        <SpaceFiller>
                          <Spinner />
                        </SpaceFiller>
                      ) : (
                        <div className="flex-1">
                          <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                            <h3 className="text-base font-semibold leading-6 text-black">
                              {translateText("devices_online", language)}
                            </h3>
                            <span className="text-xs text-secondary">
                              {translateText(
                                formattedData.devicesOnline.length === 0
                                  ? "not_enough_data"
                                  : "oversight_overview_devices_online_subtext",
                                language,
                              )}
                            </span>
                          </div>
                          <VerticalBarChart
                            data={formattedData.devicesOnline}
                            className="mx-auto text-sm lg:h-64 h-48"
                            keys={
                              formattedData.devicesOnline.length === 0
                                ? ["value"]
                                : ["Count"]
                            }
                          />
                        </div>
                      )}
                    </div>
                  </Card>
                </div>

                {/* Row 2 */}
                <div className="grid md:grid-cols-3 grid-cols-1 mt-6 md:space-x-8 space-x-0 md:space-y-0 space-y-8">
                  {/* Active users */}
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full items-center justify-between space-x-6">
                      <div className="flex-1">
                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                          <h3 className="text-base font-semibold leading-6 text-gray-900">
                            {translateText("active_users", language)}
                          </h3>
                          <span className="text-xs text-gray-400">
                            {translateText(
                              "oversight_overview_active_users_subtext",
                              language,
                            )}
                          </span>
                        </div>
                        <NumericChart
                          value={formattedData.activeUsers}
                          className="lg:h-64 md:h-48 h-32"
                          colour="info"
                        />
                      </div>
                    </div>
                  </Card>

                  {/* Actionable request status count */}
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full items-center justify-between space-x-6">
                      <div className="flex-1">
                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                          <h3 className="text-base font-semibold leading-6 text-black">
                            {translateText(
                              "actionable_request_status",
                              language,
                            )}
                          </h3>
                          <span className="text-xs text-secondary">
                            {translateText(
                              formattedData.actionableRequestsCount.length === 0
                                ? "not_enough_data"
                                : "oversight_overview_actionable_request_status_subtext",
                              language,
                            )}
                          </span>
                        </div>
                        <VerticalBarChart
                          data={formattedData.actionableRequestsCount}
                          className="mx-auto text-sm lg:h-64 h-48"
                          keys={
                            formattedData.actionableRequestsCount.length === 0
                              ? ["value"]
                              : ["Count"]
                          }
                        />
                      </div>
                    </div>
                  </Card>

                  {/* Content type count */}
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full items-center justify-between space-x-6">
                      <div className="flex-1">
                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                          <h3 className="text-base font-semibold leading-6 text-black">
                            {translateText("content_type_count", language)}
                          </h3>
                          <span className="text-xs text-secondary">
                            {translateText(
                              formattedData.contentCount.length === 0
                                ? "not_enough_data"
                                : "oversight_overview_content_type_count_subtext",
                              language,
                            )}
                          </span>
                        </div>
                        <DonutChart
                          data={formattedData.contentCount}
                          className="mx-auto text-sm lg:h-64 h-48"
                        />
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Row 3 */}
                <div className="grid md:grid-cols-3 grid-cols-1 mt-6 md:space-x-8 space-x-0 md:space-y-0 space-y-8">
                  {/* Payment transaction status frequency */}
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full items-center justify-between space-x-6">
                      <div className="flex-1">
                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                          <h3 className="text-base font-semibold leading-6 text-black">
                            {translateText("payment_status_count", language)}
                          </h3>
                          <span className="text-xs text-secondary">
                            {translateText(
                              formattedData.paymentTransactionStatusCount
                                .length === 0
                                ? "not_enough_data"
                                : "oversight_overview_payment_status_count_subtext",
                              language,
                            )}
                          </span>
                        </div>
                        <VerticalBarChart
                          data={formattedData.paymentTransactionStatusCount}
                          className="mx-auto text-sm lg:h-64 h-48"
                          keys={
                            formattedData.paymentTransactionStatusCount
                              .length === 0
                              ? ["value"]
                              : ["Count"]
                          }
                        />
                      </div>
                    </div>
                  </Card>

                  {/* Rooms that have made requests */}
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full items-center justify-between space-x-6">
                      <div className="flex-1">
                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                          <h3 className="text-base font-semibold leading-6 text-black">
                            {translateText("rooms_with_requests", language)}
                          </h3>
                          <span className="text-xs text-secondary">
                            {translateText(
                              formattedData.roomsMadeRequests.length === 0
                                ? "not_enough_data"
                                : "oversight_overview_rooms_with_requests_subtext",
                              language,
                            )}
                          </span>
                        </div>
                        <DonutChart
                          data={formattedData.roomsMadeRequests}
                          className="mx-auto text-sm lg:h-64 h-48"
                          colours={[
                            getColourPaletteHex("light-success"),
                            getColourPaletteHex("light-danger"),
                          ]}
                        />
                      </div>
                    </div>
                  </Card>

                  {/* Payment transaction amount */}
                  <Card className="overflow-x-auto scrollbar-invisible">
                    <div className="flex w-full items-center justify-between space-x-6">
                      <div className="flex-1">
                        <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                          <h3 className="text-base font-semibold leading-6 text-gray-900">
                            {translateText("payment_total_amount", language)}
                          </h3>
                          <span className="text-xs text-gray-400">
                            {translateText(
                              "oversight_overview_payment_total_amount_subtext",
                              language,
                            )}
                          </span>
                        </div>
                        <NumericChart
                          value={formattedData.paymentTotalAmount.toFixed(2)}
                          className="lg:h-64 md:h-48 h-32"
                          textSize={getTextSize(
                            formattedData.paymentTotalAmount.toFixed(2),
                          )}
                          subText={getCurrencySymbol(
                            user === null ? null : user.property.currency,
                          )}
                          colour="info"
                        />
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("not_enough_data", language)}
                subtext={translateText(
                  "not_enough_data_analytics_subtext",
                  language,
                )}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
